import { useEffect, useRef, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const EMPTY = { code: "", name: "", active: true, quiniela_multipliers: { first: 70, second: 20, third: 10 }, result_provider_code: "none" };

export default function Operators() {
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [logoBust, setLogoBust] = useState(Date.now());
  const fileRefs = useRef({});

  const uploadLogo = async (op, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Máx. 2 MB");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/operators/${op.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoBust(Date.now());
      await load();
      toast.success(`Logo actualizado para ${op.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeLogo = async (op) => {
    if (!window.confirm(`¿Eliminar el logo de ${op.name}?`)) return;
    try {
      await api.delete(`/operators/${op.id}/logo`);
      setLogoBust(Date.now());
      await load();
      toast.success("Logo eliminado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    const [o, p] = await Promise.all([
      api.get("/operators"),
      api.get("/draw-results/providers"),
    ]);
    setItems(o.data);
    setProviders(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        quiniela_multipliers: {
          first: parseFloat(form.quiniela_multipliers.first) || 0,
          second: parseFloat(form.quiniela_multipliers.second) || 0,
          third: parseFloat(form.quiniela_multipliers.third) || 0,
        },
      };
      if (editing) await api.put(`/operators/${editing.id}`, payload);
      else await api.post("/operators", payload);
      toast.success(editing ? "Actualizado" : "Creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Eliminar operador ${o.name}?`)) return;
    try { await api.delete(`/operators/${o.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Operadores de Lotería"
        subtitle="Loterías disponibles en la República Dominicana"
        testId="operators-header"
        actions={
          <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="operators-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo operador
          </Button>
        }
      />
      <div className="p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((o) => (
            <div key={o.id} className="bg-white border border-slate-200 rounded-sm p-4 flex items-start justify-between gap-3" data-testid={`operator-card-${o.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 shrink-0 border border-slate-200 rounded-sm bg-slate-50 flex items-center justify-center overflow-hidden">
                    {o.logo_path ? (
                      <img
                        src={`${API_BASE}/operators/${o.id}/logo?t=${logoBust}`}
                        alt={o.name}
                        className="max-w-full max-h-full object-contain"
                        data-testid={`operator-logo-${o.code}`}
                      />
                    ) : (
                      <ImageOff className="w-5 h-5 text-slate-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono">{o.code}</div>
                    <div className="text-lg font-bold text-slate-950" style={{ fontFamily: "Manrope" }}>{o.name}</div>
                    <div className="text-xs mt-1">
                      {o.active ? <span className="text-green-700">● Activo</span> : <span className="text-slate-400">● Inactivo</span>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2 text-[11px] font-mono">
                  <span className="bg-green-50 text-green-800 border border-green-200 rounded-sm px-2 py-0.5">1ro {o.quiniela_multipliers?.first ?? 70}x</span>
                  <span className="bg-blue-50 text-blue-800 border border-blue-200 rounded-sm px-2 py-0.5">2do {o.quiniela_multipliers?.second ?? 20}x</span>
                  <span className="bg-slate-50 text-slate-700 border border-slate-200 rounded-sm px-2 py-0.5">3ro {o.quiniela_multipliers?.third ?? 10}x</span>
                </div>
                {o.result_provider_code && o.result_provider_code !== "none" && (
                  <div className="mt-2 text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                    ⟳ Auto-sync: <span className="text-blue-700 font-bold">{o.result_provider_code}</span>
                  </div>
                )}
                <div className="mt-3 flex gap-1.5">
                  <input
                    ref={(el) => (fileRefs.current[o.id] = el)}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={(e) => uploadLogo(o, e.target.files?.[0]).finally(() => { if (fileRefs.current[o.id]) fileRefs.current[o.id].value = ""; })}
                    className="hidden"
                    data-testid={`operator-logo-input-${o.code}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileRefs.current[o.id]?.click()}
                    className="h-7 rounded-sm border-slate-300 text-[11px] font-semibold"
                    data-testid={`operator-logo-upload-${o.code}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> {o.logo_path ? "Cambiar" : "Subir"} logo
                  </Button>
                  {o.logo_path && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeLogo(o)}
                      className="h-7 rounded-sm border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-semibold"
                      data-testid={`operator-logo-remove-${o.code}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => { setEditing(o); setForm({ ...o, quiniela_multipliers: o.quiniela_multipliers || { first: 70, second: 20, third: 10 }, result_provider_code: o.result_provider_code || "none" }); setOpen(true); }} className="h-8 rounded-sm border-slate-300" data-testid={`operator-edit-${o.code}`}><Pencil className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(o)} className="h-8 rounded-sm border-red-300 text-red-700"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>{editing ? "Editar operador" : "Nuevo operador"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 rounded-sm font-mono" data-testid="operator-form-code" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="operator-form-name" />
            </div>
            <div className="pt-2 border-t border-slate-200">
              <Label className="text-xs font-semibold uppercase tracking-wider">Multiplicadores Quiniela</Label>
              <div className="text-[11px] text-slate-500 mb-2">Pagos por posición (1er, 2do, 3er lugar)</div>
              <div className="grid grid-cols-3 gap-2">
                {["first", "second", "third"].map((pos, i) => (
                  <div key={pos}>
                    <Label className="text-[10px] font-bold uppercase text-slate-600">{["1ro", "2do", "3ro"][i]}</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number" step="0.1"
                        value={form.quiniela_multipliers[pos]}
                        onChange={(e) => setForm({
                          ...form,
                          quiniela_multipliers: { ...form.quiniela_multipliers, [pos]: e.target.value },
                        })}
                        className="rounded-sm font-mono pr-6"
                        data-testid={`operator-form-mult-${pos}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono">x</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-slate-200">
              <Label className="text-xs font-semibold uppercase tracking-wider">Proveedor de resultados</Label>
              <div className="text-[11px] text-slate-500 mb-2">Origen para auto-importar resultados del sorteo</div>
              <Select value={form.result_provider_code} onValueChange={(v) => setForm({ ...form, result_provider_code: v })}>
                <SelectTrigger className="rounded-sm" data-testid="operator-form-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {providers.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="text-sm">Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="operator-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
